import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Box,
  Fab,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Link
} from '@mui/material';
import { OpenInNew as OpenInNewIcon, Add as AddIcon } from '@mui/icons-material';
import API_BASE_URL from '../config';
import './Groups.css';


const Groups = () => {
  const [groups, setGroups] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const [primaryColor, setPrimaryColor] = useState(localStorage.getItem('primaryColor') || '#007bff');
  const [secondaryColor, setSecondaryColor] = useState(localStorage.getItem('secondaryColor') || '#6c757d');
  const [darkMode, setDarkMode] = useState(localStorage.getItem('darkMode') === 'true');
  const [textColor, setTextColor] = useState(darkMode ? '#ffffff' : '#000000');
  const [openDialog, setOpenDialog] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [packageAllowance, setPackageAllowance] = useState(false);
  const [nameError, setNameError] = useState('');

  useEffect(() => {
    fetchPackageAllowance();
    fetchGroups();
  }, []);

  useEffect(() => {
      const checkLocalStorage = () => {
        const storedPrimaryColor = localStorage.getItem('primaryColor');
        const storedSecondaryColor = localStorage.getItem('secondaryColor');
        const storedDarkMode = localStorage.getItem('darkMode') === 'true';
  
        if (storedPrimaryColor !== primaryColor) {
          setPrimaryColor(storedPrimaryColor);
        }
        if (storedSecondaryColor !== secondaryColor) {
          setSecondaryColor(storedSecondaryColor);
        }
        if (storedDarkMode !== darkMode) {
          setDarkMode(storedDarkMode);
          setTextColor(storedDarkMode? '#ffffff' : '#000000');
        }
      };
  
      checkLocalStorage();
      const intervalId = setInterval(checkLocalStorage, 200);
  
      return () => clearInterval(intervalId);
    }, [primaryColor, secondaryColor, darkMode]);

    const fetchPackageAllowance = async () => {
        try {
          const response = await fetch(`${API_BASE_URL}/users/package/device_group`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });
    
          if (response.status === 401) {
            navigate('/unauthorized');
            return;
          }
    
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
    
          const data = await response.json();
    
          setPackageAllowance(data === 1);
    
        } catch (error) {
          console.error('Error fetching user package:', error);
          setError('Failed to fetch user package. Please try again later.');
        } finally {
          setLoading(false);
        }
      };

  const fetchGroups = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/device/groups`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.status === 401) {
        navigate('/unauthorized');
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const groupsData = await response.json();
      setGroups(groupsData);
      setError(null);
    } catch (error) {
      console.error('Error fetching groups:', error);
      setError('Failed to fetch groups. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddGroup = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setNewGroupName('');
    setNameError('');
  };

  const handleNewGroupNameChange = (event) => {
    const name = event.target.value;
    setNewGroupName(name);
    if (groups.some(group => group.group_name.toLowerCase() === name.toLowerCase())) {
      setNameError('A group with this name already exists');
    } else {
      setNameError('');
    }
  };

  const handlePresetClick = (presetId) => {
    if (presetId) {
      navigate(`/preset/${presetId}`);
    }
  };


  const handleCreateGroup = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/device/new-group`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ group_name: newGroupName })
      });

      if (response.status === 401) {
        navigate('/unauthorized');
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to create group');
      }

      handleCloseDialog();
      window.location.reload();
    } catch (error) {
      console.error('Error creating group:', error);
      // Handle error (e.g., show error message to user)
    }
  };


  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleGroupClick = (groupId) => {
    navigate(`/groups/${groupId}`);
  };

  if (loading) {
    return <div style={{ color: primaryColor }}>Loading groups...</div>;
  }

  if (error) {
    return <div style={{ color: primaryColor }}>Error: {error}</div>;
  }

  return (
    <div className={`groups ${darkMode ? 'dark-mode' : 'light-mode'}`} style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {!packageAllowance ? (
        <div style={{ 
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: secondaryColor,
          color: textColor,
          padding: 20,
          textAlign: 'center'
        }}>
          <div>
            <Typography variant="h4" gutterBottom>
              Group Access Restricted
            </Typography>
            <Typography variant="body1">
              This feature is not available with your current package.
            </Typography>
            <Button 
              variant="contained" 
              onClick={() => navigate('/help')}
              sx={{ 
                mt: 2,
                backgroundColor: primaryColor,
                color: textColor,
                '&:hover': {
                  backgroundColor: darkMode ? '#555' : '#e0e0e0',
                }
              }}
            >
              Contact Support
            </Button>
          </div>
        </div>
      ) : (
        <>
      <Paper sx={{ 
        flex: 1, 
        display: 'flex', 
        flexDirection: 'column', 
        backgroundColor: secondaryColor,
        overflow: 'hidden',
      }}>
        <Box sx={{ 
          flex: 1, 
          overflow: 'auto',
          '& table': {
            borderCollapse: 'separate',
            borderSpacing: 0,
          },
        }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {['GROUP NAME', 'NUMBER OF DEVICES', 'CURRENT PRESET', 'ACTIONS'].map((header) => (
                  <TableCell 
                    key={header} 
                    sx={{ 
                      backgroundColor: `${darkMode ? '#666666' : '#aaaaaa'} !important`,
                      color: `${textColor} !important`,
                      textAlign: 'center',
                    }}
                  >
                    {header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.length > 0 ? (
                groups
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((group, index) => (
                    <TableRow 
                      key={group.id} 
                      hover 
                      sx={{ 
                        backgroundColor: index % 2 ? secondaryColor : (darkMode ? '#262626' : '#f5f5f5'),
                        '&:hover': {
                          backgroundColor: darkMode ? '#333333' : '#e0e0e0',
                        },
                        height: 'auto',
                      }}
                    >
                      <TableCell align="center" sx={{ color: `${textColor} !important`, padding: '8px' }}>
                        {group.group_name}
                      </TableCell>
                      <TableCell align="center" sx={{ color: `${textColor} !important`, padding: '8px' }}>
                        {group.device_count}
                      </TableCell>
                      <TableCell align="center" sx={{ color: `${textColor} !important`, padding: '8px' }}>
                        {group.preset_id && group.preset_id !== '-1' ? (
                          <Link
                            component="button"
                            onClick={() => handlePresetClick(group.preset_id)}
                            sx={{
                              color: textColor,
                              textDecoration: 'none',
                              cursor: 'pointer',
                              '&:hover': {
                                textDecoration: 'underline',
                              },
                            }}
                          >
                            {group.preset_name}
                          </Link>
                        ) : (
                          group.preset_name
                        )}
                      </TableCell>
                      <TableCell align="center" sx={{ color: `${textColor} !important`, padding: '8px' }}>
                        <IconButton onClick={(e) => { e.stopPropagation(); handleGroupClick(group.id); }} sx={{ color: textColor }}>
                          <OpenInNewIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
              ) : (
                <TableRow>
                  <TableCell 
                    colSpan={4} 
                    align="center" 
                    sx={{ 
                      color: `${textColor} !important`, 
                      padding: '16px',
                      backgroundColor: darkMode ? '#262626' : '#f5f5f5',
                    }}
                  >
                    No groups available
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={groups.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            color: textColor,
            '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows, .MuiTablePagination-select, .MuiTablePagination-selectIcon': {
              color: `${textColor} !important`,
            },
            '.MuiTablePagination-actions': {
              color: textColor,
              '& .MuiIconButton-root.Mui-disabled': {
                color: darkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
              },
            },
          }}
        />
      </Paper>
      <Fab
        variant="extended"
        color="primary"
        aria-label="add group"
        onClick={handleAddGroup}
        sx={{
          position: 'absolute',
          bottom: 72,
          right: 16,
          backgroundColor: primaryColor,
          color: textColor,
          '&:hover': {
            backgroundColor: darkMode ? '#555' : '#e0e0e0',
          },
        }}
      >
        <AddIcon sx={{ mr: 1 }} />
        Add Group
      </Fab>
      </>
      )}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        PaperProps={{
          style: {
            backgroundColor: darkMode ? '#333' : '#fff',
            color: textColor,
          },
        }}
      >
        <DialogTitle sx={{ color: textColor }}>Add New Group</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="name"
            label="Group Name"
            type="text"
            fullWidth
            variant="outlined"
            inputProps={{ maxLength: 255 }}
            value={newGroupName}
            onChange={handleNewGroupNameChange}
            error={!!nameError}
            helperText={nameError}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: darkMode ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)',
                },
                '&:hover fieldset': {
                  borderColor: darkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: primaryColor,
                },
              },
              '& .MuiInputLabel-root': {
                color: textColor,
              },
              '& .MuiInputBase-input': {
                color: textColor,
              },
              '& .MuiFormHelperText-root': {
                color: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
              },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} sx={{ color: textColor }}>Cancel</Button>
          <Button 
            onClick={handleCreateGroup} 
            disabled={!newGroupName || !!nameError}
            sx={{
              color: !newGroupName || !!nameError 
                ? (darkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)') 
                : (darkMode ? '#4caf50' : '#45a049'),
              backgroundColor: !newGroupName || !!nameError 
                ? 'transparent'
                : (darkMode ? 'rgba(76, 175, 80, 0.1)' : 'rgba(76, 175, 80, 0.1)'), 
              '&:hover': {
                backgroundColor: !newGroupName || !!nameError 
                  ? 'transparent'
                  : (darkMode ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.2)'),
              },
            }}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Groups;